import { useEffect, useMemo, useRef, useState } from "react";
import { geoNaturalEarth1, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import worldTopo from "world-atlas/countries-110m.json";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import "../styles/world-heat-map.css";

countries.registerLocale(enLocale);

const VIEW_W = 960;
const VIEW_H = 480;

// Pre-build the GeoJSON once (top-level so it survives re-renders).
const WORLD_GEO = feature(worldTopo, worldTopo.objects.countries);

// 5-stop sequential purple scale (log-based, so a dominant country
// like India doesn't crush the contrast for everyone else).
function colorFor(count, max) {
    if (!count || count <= 0) return "#eef0f6";
    if (max <= 1) return "#6f10b9";
    const ratio = Math.log(count) / Math.log(max);
    if (ratio < 0.20) return "#ddd6fe";
    if (ratio < 0.40) return "#c4b5fd";
    if (ratio < 0.60) return "#a78bfa";
    if (ratio < 0.80) return "#8b5cf6";
    return "#6f10b9";
}

export default function WorldHeatMap({ data = [] }) {
    const containerRef = useRef(null);
    const [tooltip, setTooltip] = useState(null);

    // Build a numeric-id → { count, name } lookup from the supplied data.
    // Accepts rows with `code` (ISO-2) and/or `country` (display name) — IPinfo can store either.
    const { lookup, max, totalUsers } = useMemo(() => {
        const lk = {};
        let m = 0;
        let tot = 0;
        for (const row of data) {
            const rawCode = (row.code || "").trim().toUpperCase();
            const rawName = (row.country || "").trim();

            // Resolve to a 2-letter ISO code from whatever we have.
            let alpha2;
            if (rawCode.length === 2) alpha2 = rawCode;
            else if (rawCode.length === 3) alpha2 = countries.alpha3ToAlpha2(rawCode);
            if (!alpha2 && rawName) alpha2 = countries.getAlpha2Code(rawName, "en");

            if (!alpha2) continue;
            const numeric = countries.alpha2ToNumeric(alpha2);
            if (!numeric) continue;

            const display = countries.getName(alpha2, "en") || rawName || alpha2;
            lk[Number(numeric)] = { count: row.count, name: display };
            tot += row.count;
            if (row.count > m) m = row.count;
        }
        return { lookup: lk, max: m, totalUsers: tot };
    }, [data]);

    // Pre-compute geo paths once.
    const pathGenerator = useMemo(() => {
        const projection = geoNaturalEarth1().fitSize([VIEW_W, VIEW_H], WORLD_GEO);
        return geoPath(projection);
    }, []);

    const handleMove = (e, name, count, numeric) => {
        const rect = containerRef.current.getBoundingClientRect();
        setTooltip({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
            name,
            count: count || 0,
            pct: totalUsers > 0 && count ? (count / totalUsers) * 100 : 0,
            numeric,
        });
    };

    useEffect(() => {
        const onScroll = () => setTooltip(null);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <div
            className={`pxl-wmap${tooltip ? " is-hovering" : ""}`}
            ref={containerRef}
            onMouseLeave={() => setTooltip(null)}
        >
            <svg
                className="pxl-wmap-svg"
                viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                preserveAspectRatio="xMidYMid meet"
                role="img"
                aria-label="World map of users by country"
            >
                <g>
                    {WORLD_GEO.features.map((f, idx) => {
                        const numeric = Number(f.id);
                        const entry = lookup[numeric];
                        const count = entry?.count || 0;
                        const name = entry?.name || f.properties.name;
                        return (
                            <path
                                key={f.id != null ? `c-${f.id}` : `i-${idx}`}
                                d={pathGenerator(f)}
                                className={`pxl-wmap-country${count > 0 ? " has-data" : ""}`}
                                fill={colorFor(count, max)}
                                onMouseEnter={(e) => handleMove(e, name, count, numeric)}
                                onMouseMove={(e) => handleMove(e, name, count, numeric)}
                            />
                        );
                    })}
                </g>
                {/* Active country re-painted on top so its outline is never clipped. */}
                {tooltip && (() => {
                    const f = WORLD_GEO.features.find((x) => Number(x.id) === tooltip.numeric);
                    if (!f) return null;
                    const entry = lookup[tooltip.numeric];
                    const count = entry?.count || 0;
                    return (
                        <path
                            d={pathGenerator(f)}
                            className={`pxl-wmap-country is-active${count > 0 ? " has-data" : ""}`}
                            fill={colorFor(count, max)}
                            pointerEvents="none"
                        />
                    );
                })()}
            </svg>

            {tooltip && (
                <div
                    className="pxl-wmap-tooltip"
                    style={{ left: tooltip.x, top: tooltip.y }}
                    role="tooltip"
                >
                    <div className="pxl-wmap-tooltip-head">
                        <span className="pxl-wmap-tooltip-flag" aria-hidden="true">
                            <span className={`pxl-wmap-dot pxl-wmap-dot--${tooltip.count > 0 ? "data" : "empty"}`} />
                        </span>
                        <span className="pxl-wmap-tooltip-name">{tooltip.name}</span>
                    </div>
                    {tooltip.count > 0 ? (
                        <>
                            <div className="pxl-wmap-tooltip-count">
                                <strong>{tooltip.count.toLocaleString()}</strong>
                                {tooltip.count === 1 ? " user" : " users"}
                            </div>
                            <div className="pxl-wmap-tooltip-pct">
                                {tooltip.pct.toFixed(1)}% of mapped users
                            </div>
                        </>
                    ) : (
                        <div className="pxl-wmap-tooltip-empty">No users from this region</div>
                    )}
                </div>
            )}

            <div className="pxl-wmap-legend" aria-hidden="true">
                <span className="pxl-wmap-legend-label">Less</span>
                <span className="pxl-wmap-legend-bar">
                    <i style={{ background: "#ddd6fe" }} />
                    <i style={{ background: "#c4b5fd" }} />
                    <i style={{ background: "#a78bfa" }} />
                    <i style={{ background: "#8b5cf6" }} />
                    <i style={{ background: "#6f10b9" }} />
                </span>
                <span className="pxl-wmap-legend-label">More</span>
            </div>
        </div>
    );
}
